import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { BehaviorSubject, combineLatest, of, Subject, switchMap, tap } from 'rxjs';
import { Stack } from 'src/app/common/dataStructures/stack';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { ToggleOption } from 'src/app/components/option-toggle/option-toggle.component';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { formatFromDateWithSeconds } from 'src/app/common/datetime_utility';
import { aiFindingsTableColumns, aiFindingsActionColumn } from 'src/app/models/tableColumns/aiFindingsTableColumn';
import { AiFindingsTableRowViewModel } from 'src/app/models/viewModels/aiFindingsTableRowViewModel';
import { AiFindingsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiFindingsFilters.model';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { FindingModel } from 'src/app/models/api/finding.model';
import { findingAttackToDisplayName } from 'src/app/common/findingRulesUtils';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { owaspLlmToDisplayText, owaspAsiToDisplayText } from 'src/app/common/violationRulesUtils';
import {
  getAiServiceLogoPath,
  getClientApplicationIconPath,
  isCustomAgentApplication,
} from 'src/app/common/aiServiceUtils';
import { AgentClientApplication } from 'src/app/models/api/agentStep.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { extractContinuationTokenFromURL } from 'src/app/common/exractContinuationTokenFromURL';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { UnifiedPipelineService, skipFirstRun } from 'src/app/services/infra/unifiedPipeline/unified-pipeline.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { RSA_DEMO_FINDING_AI_SERVICES } from 'src/app/models/selectOptions/rsaDemoAiServices';

@Component({
  selector: 'app-enterprise-ai-findings-table',
  templateUrl: './enterprise-ai-findings-table.component.html',
  styleUrls: ['./enterprise-ai-findings-table.component.scss'],
})
export class EnterpriseAiFindingsTableComponent implements OnInit {
  public sourceToggle = input<ToggleOption>('option1');
  public totalItems = output<number>();
  public blockActionsCount = output<number>();

  public columns = signal<ITableColumn[]>([]);
  public findingsCurrentPage = signal<AiFindingsTableRowViewModel[]>([]);
  public findingsCurrentPage$: BehaviorSubject<AiFindingsTableRowViewModel[]>;
  public isLoading = signal<boolean>(true);
  public showNoData = signal<boolean>(false);

  public readonly defaultPageSize = signal<number>(this._aiFindingsHttpService.defaultPageSize);
  public pageFilters = signal<AiFindingsFiltersModel>(
    this._filtersService.getFilters(PageWithFiltersEnum.AiFindings) as AiFindingsFiltersModel
  );
  public pageInfo = signal<ITablePageInfo>({
    number: 1,
    currSize: 0,
    maxSize: this.defaultPageSize(),
    totalItems: 0,
    totalPages: 1,
  });

  private readonly _notAvailableString: string;

  private _paginationNavigation = signal<IPaginationNavigationModel | null>(null);
  private _pageChange$: Subject<string> = new Subject<string>();
  private _sourceToggleChange$: Subject<ToggleOption> = new Subject<ToggleOption>();
  private readonly _httpClient = inject(HttpClient);
  private readonly _featureFlagsService = inject(FeatureFlagsService);
  private _rsaMockFindings: FindingModel[] | null = null;
  private _iamDemoMockFindings: FindingModel[] | null = null;
  private _isOnFirstPage = true;

  constructor(
    private readonly _sidePanel: SidePanelService,
    private readonly _logger: LoggerService,
    private readonly _aiFindingsHttpService: AiFindingsHttpService,
    private readonly _filtersService: FiltersService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _storeService: StoreService,
    private readonly _unifiedPipelineService: UnifiedPipelineService
  ) {
    this._notAvailableString = this._labelsProviderPipe.transform('common.notAvailable');
    this.findingsCurrentPage$ = this._storeService.getFindingsPageDataAsSubject$();
    this._initializeColumns();
    effect(() => {
      this.findingsCurrentPage$.next(this.findingsCurrentPage());
    });

    // Effect to route source toggle changes through Subject (skip initial run)
    const onSourceToggleChange = skipFirstRun((currentToggle: ToggleOption) => {
      if (this._unifiedPipelineService.isEnabled()) {
        this._sourceToggleChange$.next(currentToggle);
      }
    });
    effect(() => onSourceToggleChange(this.sourceToggle()), { allowSignalWrites: true });

    // Stream for source toggle changes - uses switchMap to cancel previous requests
    this._sourceToggleChange$
      .pipe(
        tap(() => {
          this.findingsCurrentPage.set([]);
          this.isLoading.set(true);
          this._paginationNavigation.set(null);
        }),
        switchMap((currentToggle: ToggleOption) => {
          const filters = this.pageFilters().getSelectedFilters();
          const filtersWithSource = this._unifiedPipelineService.applySourceFilter(filters, currentToggle);
          if (this._isOnlyRsaServicesSelected(filtersWithSource)) {
            return of(this._emptyResponse());
          }
          const cleanedFilters = this._stripRsaServicesFromFilters(filtersWithSource);
          return this._aiFindingsHttpService.startGetFindings(cleanedFilters, this.pageFilters().sortedColumn$.value);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<FindingModel>) => this._handleAgentStepCollectionResponse(response),
        error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
      });
  }

  ngOnInit(): void {
    const { onUpdate$, sortedColumn$ } = this.pageFilters();
    // Stream 1: onUpdate + sort changes -> startGetFindings
    combineLatest([onUpdate$, sortedColumn$])
      .pipe(
        tap(() => {
          this.findingsCurrentPage.set([]);
          this.isLoading.set(true);
          this._isOnFirstPage = true;
        }),
        switchMap(([filters, sort]: [ISelectedFilters, SortColumn | undefined]) => {
          const filtersWithSource = this._unifiedPipelineService.applySourceFilter(filters, this.sourceToggle());
          if (this._isOnlyRsaServicesSelected(filtersWithSource)) {
            return of(this._emptyResponse());
          }
          const cleanedFilters = this._stripRsaServicesFromFilters(filtersWithSource);
          return this._aiFindingsHttpService.startGetFindings(cleanedFilters, sort);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<FindingModel>) => this._handleAgentStepCollectionResponse(response),
        error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
      });

    // Stream 2: page changes -> getFindingsByContinuationToken
    this._pageChange$
      .pipe(
        tap(() => {
          this.findingsCurrentPage.set([]);
          this.isLoading.set(true);
        }),
        switchMap((token: string) => {
          const sp = this.sourceToggle() === 'option2' ? 'inline' : undefined;
          return this._aiFindingsHttpService.getFindingsByContinuationToken(token, sp);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<FindingModel>) => this._handleAgentStepCollectionResponse(response),
        error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
      });

    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public onPagination(operation: PaginationOperationEnum) {
    if (!this.isLoading()) {
      const navigation = this._paginationNavigation();
      if (!navigation) return;

      if (operation == PaginationOperationEnum.next) {
        navigation.previousPage.push(navigation.currentPage);
      }

      const pageUrl = operation == PaginationOperationEnum.next ? navigation.nextPage : navigation.previousPage.pop();

      this._isOnFirstPage = operation !== PaginationOperationEnum.next && navigation.previousPage.size() === 0;

      const continuationToken = extractContinuationTokenFromURL(pageUrl ?? '', this._logger);
      if (continuationToken) {
        this._pageChange$.next(continuationToken);
      }
    }
  }

  public rowSelected(selection: AiFindingsTableRowViewModel): void {
    this._sidePanel.openFindingDetails(selection);
  }

  public rowUnselected() {
    this._sidePanel.close();
  }

  private _handleAgentStepCollectionResponse(response: JsonHalCollection<FindingModel>) {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);
    if (response.aggregations) {
      this.blockActionsCount.emit(response.aggregations['blockActions'] ?? 0);
    }

    const items = response._embedded.items;
    const viewModels = items?.length ? items.map((item) => this._transformResourceToViewModel(item)) : [];
    this.showNoData.set(viewModels.length === 0);
    this._loadAllMockFindings(viewModels);
  }

  private _fetchRequestOnError(error: HttpErrorResponse) {
    this.findingsCurrentPage.set([]);
    this._storeService.setFindingsPageData([]);
    this.isLoading.set(false);
    this.showNoData.set(true);
    this._httpErrorHandlerService.handle(error);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    const currentNavigation = this._paginationNavigation();

    if (!currentNavigation) {
      this._paginationNavigation.set({
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel);
    } else {
      const updatedNavigation = {
        ...currentNavigation,
        currentPage: links.self.href,
        nextPage: links.next?.href,
      };
      this._paginationNavigation.set(updatedNavigation);
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    this.totalItems.emit(pageInfo.totalItems);
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize());
    this.pageInfo.set(tablePageInfo);
  }

  private _initializeColumns(): void {
    const columns = [...aiFindingsTableColumns];
    if (this._unifiedPipelineService.isEnabled()) {
      const severityIndex = columns.findIndex((col) => col.propertyKey === 'severity');
      if (severityIndex !== -1) {
        columns.splice(severityIndex + 1, 0, aiFindingsActionColumn);
      } else {
        columns.push(aiFindingsActionColumn);
      }
    }
    this.columns.set(columns);
  }

  private _getActionDisplayInfo(action: string | undefined): { displayText: string; iconPath: string } {
    if (action === 'block') {
      return {
        displayText: 'Blocked',
        iconPath: 'assets/images/dataflow-icons/blocked.svg',
      };
    }
    return {
      displayText: 'Detected',
      iconPath: 'assets/images/dataflow-icons/shield-alert.svg',
    };
  }

  private _transformResourceToViewModel(finding: FindingModel): AiFindingsTableRowViewModel {
    const relatedStep = finding.relatedSteps?.[0];
    return {
      rowId: finding.id,
      id: finding.id,
      integrationId: finding.integration.id,
      timestamp: finding.timestamp ? formatFromDateWithSeconds(finding.timestamp) : this._notAvailableString,
      threadId: finding.thread?.id,
      actorName: relatedStep?.mainActor?.displayName,
      menuActions: [],
      platformIcon: isCustomAgentApplication(relatedStep?.clientContext?.clientApplication)
        ? 'assets/images/logo/agent-client-app-other.svg'
        : getAiServiceLogoPath(finding.rule.service),
      clickable: false,
      severity: finding.rule.severity.toString(),
      displayName: finding.rule.displayName,
      owasp: owaspLlmToDisplayText(finding.rule.owasp ?? []),
      owaspAsi: owaspAsiToDisplayText(finding.rule.owasp ?? []),
      mitreAttack: finding.rule.genAiAttack
        ?.map((attack) => this._beautifyDisplayStringPipe.transform(findingAttackToDisplayName[attack.toUpperCase()]))
        .join(', '),
      finding: finding,
      clientApplicationIcon: getClientApplicationIconPath(relatedStep?.clientContext?.clientApplication),
      clientApplication: Object.values(AgentClientApplication).includes(
        relatedStep?.clientContext?.clientApplication as AgentClientApplication
      )
        ? this._displayCamelCaseStringPipe.transform(
            AgentClientApplication[relatedStep?.clientContext?.clientApplication]
          )
        : this._displayCamelCaseStringPipe.transform(relatedStep?.clientContext?.clientApplication),
      labels: finding.labelBindings || [],
      action: this._getActionDisplayInfo(finding.action).displayText,
      actionIcon: this._getActionDisplayInfo(finding.action).iconPath,
    } as AiFindingsTableRowViewModel;
  }

  private _loadAllMockFindings(apiViewModels: AiFindingsTableRowViewModel[]): void {
    if (!this._isOnFirstPage) {
      this._setFindingsPage(apiViewModels);
      return;
    }

    const rsaEnabled = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
    const iamDemoEnabled = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex);

    if (!rsaEnabled && !iamDemoEnabled) {
      this._setFindingsPage(apiViewModels);
      return;
    }

    const rsaReady = !rsaEnabled || !!this._rsaMockFindings;
    const iamDemoReady = !iamDemoEnabled || !!this._iamDemoMockFindings;

    if (rsaReady && iamDemoReady) {
      this._prependAllMockFindings(apiViewModels);
      return;
    }

    const loads: import('rxjs').Observable<Record<string, FindingModel>>[] = [];

    if (rsaEnabled && !this._rsaMockFindings) {
      loads.push(this._httpClient.get<Record<string, FindingModel>>('assets/mockData/rsaFindingsMockData.json'));
    }
    if (iamDemoEnabled && !this._iamDemoMockFindings) {
      loads.push(this._httpClient.get<Record<string, FindingModel>>('assets/mockData/iamDemoFindingsMockData.json'));
    }

    combineLatest(loads).subscribe({
      next: (results) => {
        let idx = 0;
        if (rsaEnabled && !this._rsaMockFindings) {
          this._rsaMockFindings = Object.values(results[idx++]);
        }
        if (iamDemoEnabled && !this._iamDemoMockFindings) {
          this._iamDemoMockFindings = this._applyDynamicTimestamps(Object.values(results[idx]));
        }
        this._prependAllMockFindings(apiViewModels);
      },
      error: () => this._setFindingsPage(apiViewModels),
    });
  }

  private _prependAllMockFindings(apiViewModels: AiFindingsTableRowViewModel[]): void {
    const filters = this.pageFilters().getSelectedFilters();
    const selectedServices = new Set(filters.aiService ?? []);
    const hasServiceFilter = selectedServices.size > 0;
    const currentToggle = this.sourceToggle();
    const isUnified = this._unifiedPipelineService.isEnabled();

    let allMockFindings: FindingModel[] = [];

    if (this._iamDemoMockFindings) {
      let iamDemoFindings = this._iamDemoMockFindings;

      if (isUnified) {
        const isInlineMode = currentToggle === 'option2';
        if (!isInlineMode) {
          iamDemoFindings = iamDemoFindings.filter((f) => f.source !== 'inline');
        }
      } else {
        iamDemoFindings = iamDemoFindings.filter((f) => f.source !== 'inline');
      }

      allMockFindings = [...allMockFindings, ...iamDemoFindings];
    }

    if (this._rsaMockFindings) {
      allMockFindings = [...allMockFindings, ...this._rsaMockFindings];
    }

    if (hasServiceFilter) {
      const demoSet = new Set<string>([...RSA_DEMO_FINDING_AI_SERVICES, 'homegrown', 'langchain']);
      const hasDemoSelected = [...selectedServices].some((s) => demoSet.has(s));
      if (!hasDemoSelected) {
        this._setFindingsPage(apiViewModels);
        return;
      }
      allMockFindings = allMockFindings.filter((f) => selectedServices.has(f.rule.service));
    }

    const mockViewModels = allMockFindings.map((f) => this._transformMockFindingToViewModel(f));
    const merged = [...mockViewModels, ...apiViewModels].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    this._setFindingsPage(merged);
  }

  private _applyDynamicTimestamps(findings: FindingModel[]): FindingModel[] {
    const now = new Date();
    const detectBase = new Date(now);
    detectBase.setMinutes(now.getMinutes() - 10);
    detectBase.setSeconds(detectBase.getSeconds() - 37);
    const blockBase = new Date(now);
    blockBase.setMinutes(now.getMinutes() - 5);
    blockBase.setSeconds(blockBase.getSeconds() - 12);

    let detectIdx = 0;
    let blockIdx = 0;

    return findings.map((f) => {
      const isBlock = f.action === 'block';
      const base = isBlock ? blockBase : detectBase;
      const idx = isBlock ? blockIdx++ : detectIdx++;
      const ts = new Date(base.getTime() - idx * 15 * 1000);
      const updated = { ...f, timestamp: ts };
      if (updated.relatedSteps?.length) {
        updated.relatedSteps = updated.relatedSteps.map((s) => ({
          ...s,
          timestamp: ts,
        }));
      }
      return updated;
    });
  }

  private _setFindingsPage(viewModels: AiFindingsTableRowViewModel[]): void {
    this.showNoData.set(viewModels.length === 0);
    this.findingsCurrentPage.set(viewModels);
    this._storeService.setFindingsPageData(viewModels);
    this.isLoading.set(false);
  }

  private _transformMockFindingToViewModel(finding: FindingModel): AiFindingsTableRowViewModel {
    const relatedStep = finding.relatedSteps?.[0];
    const actorName = relatedStep?.mainActor?.displayName ?? finding.mainActor?.displayName ?? '';
    return {
      rowId: finding.id,
      id: finding.id,
      integrationId: finding.integration.id,
      timestamp: finding.timestamp ? formatFromDateWithSeconds(finding.timestamp) : this._notAvailableString,
      threadId: finding.thread?.id,
      actorName,
      menuActions: [],
      platformIcon: isCustomAgentApplication(relatedStep?.clientContext?.clientApplication)
        ? 'assets/images/logo/agent-client-app-other.svg'
        : getAiServiceLogoPath(finding.rule.service),
      clickable: false,
      severity: finding.rule.severity.toString(),
      displayName: finding.rule.displayName,
      owasp: owaspLlmToDisplayText(finding.rule.owasp ?? []),
      owaspAsi: owaspAsiToDisplayText(finding.rule.owasp ?? []),
      mitreAttack: finding.rule.genAiAttack
        ?.map((a) => this._beautifyDisplayStringPipe.transform(findingAttackToDisplayName[a.toUpperCase()]))
        .join(', '),
      finding,
      clientApplicationIcon: relatedStep
        ? getClientApplicationIconPath(relatedStep.clientContext.clientApplication)
        : '',
      clientApplication: relatedStep
        ? this._displayCamelCaseStringPipe.transform(relatedStep.clientContext.clientApplication)
        : '',
      labels: finding.labelBindings || [],
      action: this._getActionDisplayInfo(finding.action).displayText,
      actionIcon: this._getActionDisplayInfo(finding.action).iconPath,
    } as AiFindingsTableRowViewModel;
  }

  private _rsaDemoServiceSet = new Set<string>([...RSA_DEMO_FINDING_AI_SERVICES, 'langchain']);

  private _stripRsaServicesFromFilters(filters: ISelectedFilters): ISelectedFilters {
    if (!filters.aiService?.length) {
      return filters;
    }
    const cleaned = filters.aiService.filter((s) => !this._rsaDemoServiceSet.has(s));
    return { ...filters, aiService: cleaned.length ? cleaned : undefined };
  }

  private _isOnlyRsaServicesSelected(filters: ISelectedFilters): boolean {
    if (!filters.aiService?.length) {
      return false;
    }
    return filters.aiService.every((s) => this._rsaDemoServiceSet.has(s));
  }

  private _emptyResponse(): JsonHalCollection<FindingModel> {
    return {
      _embedded: { items: [] },
      _links: { self: { href: '' }, next: { href: '' } },
      page: { number: 1, size: 0, totalItems: 0, totalPages: 1 },
    } as JsonHalCollection<FindingModel>;
  }
}
